<template>
  <div
    class="otp-input d-flex justify-center"
    :class="{ 'otp-input--error': error }"
  >
    <input
      v-for="(digit, index) in length"
      :key="index"
      :ref="`box-${index}`"
      v-model="digits[index]"
      class="otp-box"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      maxlength="1"
      :disabled="disabled"
      @input="onInput(index, $event)"
      @keydown="onKeydown(index, $event)"
      @paste="onPaste($event)"
      @focus="$event.target.select()"
    />
  </div>
</template>

<script>

export default {
  name: 'OtpInput',
  props: {
    value: {
      type: String,
      default: '',
    },
    length: {
      type: Number,
      default: 6,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      digits: new Array(this.length).fill(''),
    }
  },
  computed: {
    joined() {
      return this.digits.join('')
    },
  },
  watch: {
    // let the parent clear or preset the code (e.g. after a rejected code)
    value(next) {
      if (next === this.joined) {
        return
      }
      this.fill(next || '')
    },
  },
  mounted() {
    if (this.value) {
      this.fill(this.value)
    }
    if (this.autofocus) {
      this.focusBox(0)
    }
  },
  methods: {
    onInput(index, event) {
      // keep digits only; a pasted or auto-filled value spills into the next boxes
      const typed = (event.target.value || '').replace(/\D/g, '')

      if (typed.length > 1) {
        this.fill(this.joined.slice(0, index) + typed, index)

        return
      }

      this.$set(this.digits, index, typed)
      this.emit()

      if (typed && index < this.length - 1) {
        this.focusBox(index + 1)
      }
    },
    onKeydown(index, event) {
      if (event.key === 'Backspace') {
        if (this.digits[index]) {
          return
        }

        // stepping back over an empty box clears the previous one
        event.preventDefault()
        if (index > 0) {
          this.$set(this.digits, index - 1, '')
          this.emit()
          this.focusBox(index - 1)
        }

        return
      }
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        this.focusBox(index - 1)
      }
      if (event.key === 'ArrowRight' && index < this.length - 1) {
        event.preventDefault()
        this.focusBox(index + 1)
      }
    },
    onPaste(event) {
      event.preventDefault()
      const pasted = (event.clipboardData || window.clipboardData).getData('text') || ''
      this.fill(pasted.replace(/\D/g, ''))
    },
    fill(raw, from = 0) {
      const chars = String(raw).replace(/\D/g, '').slice(0, this.length).split('')
      for (let i = 0; i < this.length; i += 1) {
        this.$set(this.digits, i, chars[i] || '')
      }
      this.emit()

      const next = Math.min(Math.max(chars.length, from), this.length - 1)
      this.focusBox(next)
    },
    emit() {
      this.$emit('input', this.joined)
      if (this.joined.length === this.length) {
        this.$emit('complete', this.joined)
      }
    },
    focusBox(index) {
      this.$nextTick(() => {
        const box = this.$refs[`box-${index}`]
        const el = Array.isArray(box) ? box[0] : box
        if (el) {
          el.focus()
          el.select()
        }
      })
    },
    clear() {
      this.fill('')
      this.focusBox(0)
    },
  },
}
</script>

<style scoped>
/* digits read left to right even though the app is RTL */
.otp-input {
  direction: ltr;
  gap: 8px;
}

.otp-box {
  width: 46px;
  height: 52px;
  border: 1px solid rgba(94, 86, 105, 0.32);
  border-radius: 6px;
  text-align: center;
  font-size: 1.35rem;
  font-weight: 600;
  letter-spacing: 0;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  caret-color: currentColor;
}

.theme--dark .otp-box {
  border-color: rgba(231, 227, 252, 0.28);
  color: rgba(231, 227, 252, 0.95);
  background-color: transparent;
}

.otp-box:focus {
  border-color: var(--v-primary-base);
  box-shadow: 0 0 0 2px rgba(145, 85, 253, 0.18);
}

.otp-box:disabled {
  opacity: 0.5;
}

.otp-input--error .otp-box {
  border-color: var(--v-error-base);
}

@media (max-width: 400px) {
  .otp-box {
    width: 38px;
    height: 46px;
    font-size: 1.15rem;
  }
}
</style>
